import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import placesApi from "@/api/placesApi";
import PlaceForm from "@/components/PlaceForm/PlaceForm";
import ApiError from "@/model/ApiError";
import PlaceRequest from "@/model/PlaceRequest";
import PlaceResponse from "@/model/PlaceResponse";

function EditPlacePage() {
  const { t } = useTranslation();
  const { session } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [place, setPlace] = useState<PlaceResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<"notFound" | "generic" | null>(null);

  useEffect(() => {
    if (!session || !id) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    placesApi
      .getPlaceById(session.accessToken, id)
      .then((result) => {
        if (!cancelled) {
          setPlace(result);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof ApiError && err.status === 404 ? "notFound" : "generic");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session, id]);

  if (!session || !id) {
    return null;
  }

  if (isLoading) {
    return <p>{t("places.edit.loading")}</p>;
  }

  if (error || !place) {
    return (
      <div>
        <p role="alert">
          {error === "notFound" ? t("places.edit.notFound") : t("places.edit.loadError")}
        </p>
        <Link to="/places">{t("places.form.cancel")}</Link>
      </div>
    );
  }

  const handleSubmit = async (request: PlaceRequest) => {
    await placesApi.updatePlace(session.accessToken, id, request);
    navigate("/places", { replace: true });
  };

  return (
    <PlaceForm
      title={t("places.edit.title")}
      submitLabel={t("places.edit.submit")}
      initialPlace={place}
      onSubmit={handleSubmit}
    />
  );
}

export default EditPlacePage;
